<template>
  <v-container class="py-7">
    <v-row>
      <v-col cols="12">
        <div class="title-container">
          <div class="title-block">
            <span class="page-title">{{ pageTitle }}</span>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbs" />
          </div>
          <div class="button-group">
            <v-btn v-if="isNew && $check(user, ['judiciaryusers.create'])" color="primary" elevation="0" :loading="saving" @click="create">Criar e convidar</v-btn>
            <v-btn v-if="!isNew && $check(user, ['judiciaryusers.update'])" color="primary" elevation="0" :loading="saving" @click="update">Salvar</v-btn>
            <v-btn color="primary" outlined elevation="0" to="/admins-juizo">Voltar</v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-form ref="form" v-model="valid">
      <v-row>
        <v-col cols="12" md="8">
          <v-card>
            <v-card-title>Identificação e acesso</v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.name" label="Nome" dense outlined :rules="[$rules.required]" :disabled="readonly" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.email" label="E-mail institucional" type="email" dense outlined :rules="[$rules.required, $rules.email]" :disabled="readonly" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.title" label="Cargo / função" dense outlined :disabled="readonly" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select v-model="form.role" :items="roles" item-value="id" item-text="name" label="Perfil" dense outlined :rules="[$rules.required]" :disabled="readonly" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select v-model="form.judiciary_unit_id" :items="units" item-value="id" item-text="name" label="Unidade judicial" dense outlined :rules="[$rules.required]" :disabled="readonly" />
                </v-col>
                <v-col v-if="$check(user, ['judiciaryusers.create'])" cols="12" md="6" class="d-flex align-center">
                  <v-btn text color="primary" @click="unitDialog = true"><v-icon left>mdi-plus</v-icon>Cadastrar unidade judicial</v-btn>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select v-model="form.client_id" :items="clients" item-value="id" item-text="name" label="Caso autorizado" dense outlined :rules="[$rules.required]" :disabled="readonly" />
                </v-col>
                <v-col v-if="!isNew" cols="12" md="6">
                  <v-select v-model="form.status" :items="statuses" item-value="id" item-text="name" label="Status" dense outlined :disabled="readonly" />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card color="theme-card">
            <v-card-title class="card-group-title">Segurança</v-card-title>
            <v-card-text>
              <p class="mb-3">O usuário define a própria senha usando um código de 6 dígitos enviado ao e-mail cadastrado.</p>
              <p v-if="!isNew"><strong>Último acesso:</strong> {{ form.last_login_at || "Nunca acessou" }}</p>
              <p v-if="!isNew"><strong>Conta verificada:</strong> {{ form.account_verified_at ? "Sim" : "Não" }}</p>
              <v-btn v-if="!isNew && Number(form.status) === 0 && $check(user, ['judiciaryusers.update'])" block outlined color="primary" @click="resendInvite">
                Reenviar código de ativação
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <v-dialog v-model="unitDialog" max-width="620px">
      <v-card>
        <v-card-title>Nova unidade judicial</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12"><v-text-field v-model="unitForm.name" label="Nome da unidade" dense outlined /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="unitForm.tribunal" label="Tribunal" dense outlined /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="unitForm.court_division" label="Vara / órgão julgador" dense outlined /></v-col>
            <v-col cols="12" md="8"><v-text-field v-model="unitForm.district" label="Comarca" dense outlined /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="unitForm.state" label="UF" maxlength="2" dense outlined /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="unitDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="savingUnit" @click="createUnit">Cadastrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "JudiciaryUserDetails",
  data: () => ({
    user: null,
    valid: true,
    saving: false,
    savingUnit: false,
    unitDialog: false,
    clients: [],
    units: [],
    form: { name: "", email: "", title: "", role: "COURT_STAFF", judiciary_unit_id: null, client_id: null, status: 0 },
    unitForm: { name: "", tribunal: "", court_division: "", district: "", state: "" },
    roles: [
      { id: "JUDGE", name: "Juiz(a)" },
      { id: "COURT_STAFF", name: "Servidor(a)" },
      { id: "COURT_ADMIN", name: "Administrador(a) da unidade" },
    ],
    statuses: [
      { id: 0, name: "Convidado" },
      { id: 1, name: "Ativo" },
      { id: 2, name: "Bloqueado" },
      { id: 3, name: "Inativo" },
    ],
  }),
  computed: {
    isNew() { return this.$route.params.id === "novo"; },
    readonly() { return !this.isNew && !this.$check(this.user, ["judiciaryusers.update"]); },
    pageTitle() { return this.isNew ? "Novo usuário do Juízo" : (this.form.name || "Usuário do Juízo"); },
    breadcrumbs() {
      return [
        { text: "Homepage", disabled: false, to: "/home", exact: true },
        { text: "Usuários do Juízo", disabled: false, to: "/admins-juizo", exact: true },
        { text: this.isNew ? "Incluir" : "Detalhes", disabled: true },
      ];
    },
  },
  created() {
    this.user = this.$root.user.data || this.$root.user;
    this.loadReferences();
    if (!this.isNew) this.load();
  },
  methods: {
    async loadReferences() {
      try {
        const [clients, units] = await Promise.all([
          axios.get("/api/clients", { params: { start: 1, limit: 100000, search: {} } }),
          axios.get("/api/admin/judiciary-units"),
        ]);
        this.clients = clients.data.content.data;
        this.units = units.data.content;
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2500 });
      }
    },
    async load() {
      try {
        const response = await axios.get(`/api/admin/judiciary-users/${this.$route.params.id}`);
        this.form = response.data.content;
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2500 });
      }
    },
    payload() {
      return {
        name: this.form.name, email: this.form.email, title: this.form.title,
        role: this.form.role, judiciary_unit_id: this.form.judiciary_unit_id,
        client_id: this.form.client_id, status: this.form.status,
      };
    },
    async create() {
      if (!this.$refs.form.validate()) return;
      const confirmed = await this.$dialog.confirm({
        title: "Cadastrar usuário do Juízo",
        text: "O usuário receberá por e-mail um código de ativação de 6 dígitos.",
        actions: { false: "Cancelar", true: "Cadastrar" },
      });
      if (!confirmed) return;
      this.saving = true;
      try {
        await axios.post("/api/admin/judiciary-users", this.payload());
        this.$dialog.notify.info("Usuário cadastrado e código de ativação enviado.", { position: "top-right", timeout: 3000 });
        this.$router.replace("/admins-juizo");
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 3000 });
      } finally {
        this.saving = false;
      }
    },
    async update() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        await axios.patch(`/api/admin/judiciary-users/${this.$route.params.id}`, this.payload());
        this.$dialog.notify.info("Usuário atualizado.", { position: "top-right", timeout: 2500 });
        this.$router.replace("/admins-juizo");
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 3000 });
      } finally {
        this.saving = false;
      }
    },
    async resendInvite() {
      try {
        await axios.post(`/api/admin/judiciary-users/${this.$route.params.id}/resend-invite`);
        this.$dialog.notify.info("Código de ativação enviado.", { position: "top-right", timeout: 2500 });
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2500 });
      }
    },
    async createUnit() {
      if (!this.unitForm.name || !this.unitForm.tribunal) {
        this.$dialog.notify.error("Nome e tribunal são obrigatórios.", { position: "top-right", timeout: 2500 });
        return;
      }
      this.savingUnit = true;
      try {
        const response = await axios.post("/api/admin/judiciary-units", this.unitForm);
        await this.loadReferences();
        this.form.judiciary_unit_id = response.data.content.id;
        this.unitDialog = false;
        this.unitForm = { name: "", tribunal: "", court_division: "", district: "", state: "" };
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2500 });
      } finally {
        this.savingUnit = false;
      }
    },
  },
};
</script>
